import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import logo from '../../assets/Venzo-Logo.png.webp';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { showError } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    console.log('LoginPage: isAuthenticated changed:', isAuthenticated);
    if (isAuthenticated) {
      console.log('LoginPage: Redirecting to dashboard because isAuthenticated is true');
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Load saved credentials if Remember Me was checked
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedPassword = localStorage.getItem('rememberedPassword');
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRemember(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert({ show: false, message: '', type: '' });
    setLoading(true);

    try {
      // LOGIN API CALL: '/users/login'
      const { data } = await api.post('/users/login', { email, password });
      console.log('LoginPage: Login API success', data);

      // CRITICAL FIX: Manually save to localStorage to ensure it persists
      try {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('session_owner', 'user');
        console.log('LoginPage: Manually saved to localStorage');

        // Verify immediate read
        if (!localStorage.getItem('token')) {
          console.error('LoginPage: FATAL - localStorage write failed!');
          showError('Browser storage error: Could not save login session. Please disable Private Mode or clear cache.');
          setLoading(false);
          return;
        }
      } catch (e) {
        console.error('LoginPage: Error saving to localStorage', e);
      }

      login(data.token, data.user);

      // Handle Remember Me
      if (remember) {
        localStorage.setItem('rememberedEmail', email);
        localStorage.setItem('rememberedPassword', password);
      } else {
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedPassword');
      }

      setLoading(false);
      setAlert({ show: true, message: 'Login successful! Redirecting...', type: 'success' });

      // Delay navigation to let user see the message
      setTimeout(() => {
        console.log('LoginPage: Manual navigation to dashboard triggering');
        // Force full reload to ensure auth state is picked up cleanly and avoid router issues
        window.location.href = '/dashboard';
      }, 500);
    } catch (err) {
      console.error('LoginPage: Login failed', err);
      setLoading(false);
      setAlert({
        show: true,
        message: err?.response?.data?.message || 'Login failed. Please check your credentials.',
        type: 'error'
      });
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '0 16px'
    }}>
      {/* Status bar space */}
      <div style={{ height: 32 }} />

      {/* Logo */}
      <img src={logo} alt="Venzo Logo"
        style={{ width: 90, margin: '12px auto 16px auto', display: 'block' }}
      />

      {/* Alert message */}
      {alert.show && (
        <div style={{
          marginBottom: 18,
          background: alert.type === 'success' ? '#e3ffe8' : '#ffe1e1',
          color: alert.type === 'success' ? '#187439' : '#b30000',
          padding: "10px 15px",
          borderRadius: 7,
          fontSize: 15,
          fontWeight: 500,
          border: alert.type === 'success' ? '1.2px solid #94e7b1' : '1.2px solid #fbbebe',
          width: '100%',
          maxWidth: 340,
          textAlign: 'center'
        }}>{alert.message}</div>
      )}

      {/* Login box */}
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 350,
          background: 'transparent',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <h1 style={{
          fontWeight: 700,
          fontSize: 28,
          marginBottom: 6,
          marginTop: 0,
          color: '#222'
        }}>
          Welcome!
        </h1>
        <div style={{
          color: '#667085',
          marginBottom: 22,
          fontSize: 16
        }}>Login in to continue</div>

        {/* Email field */}
        <label style={{
          fontWeight: 500,
          fontSize: 13,
          letterSpacing: 1,
          color: '#333',
          marginBottom: 4
        }}>
          EMAIL
        </label>
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '13px 40px 13px 12px',
              border: '1.5px solid #e1e1e1',
              borderRadius: 6,
              fontSize: 16,
              outline: 'none',
              background: '#fff'
            }}
            autoFocus
            disabled={loading}
          />
          {email && (
            <span style={{
              position: 'absolute',
              right: 12,
              top: 13,
              color: 'green',
              fontSize: 18
            }}>
              &#10003;
            </span>
          )}
        </div>

        {/* Password field */}
        <label style={{
          fontWeight: 500,
          fontSize: 13,
          letterSpacing: 1,
          color: '#333',
          marginBottom: 4
        }}>
          PASSWORD
        </label>
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <input
            type={showPwd ? "text" : "password"}
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '13px 40px 13px 12px',
              border: '1.5px solid #e1e1e1',
              borderRadius: 6,
              fontSize: 16,
              outline: 'none',
              background: '#fff'
            }}
            disabled={loading}
          />
          <span
            onClick={() => setShowPwd(v => !v)}
            style={{
              position: 'absolute',
              right: 12,
              top: 13,
              color: '#ccc',
              fontSize: 18,
              cursor: 'pointer',
              userSelect: 'none'
            }}
            title={showPwd ? "Hide password" : "Show password"}
          >
            {showPwd ? (
              <svg width="20" height="20" fill="none" stroke="#bbb" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M17.94 17.94A10.02 10.02 0 0 1 12 20C7 20 2.73 16.11 1 12a19.84 19.84 0 0 1 3.59-4.77M9.88 9.88A3 3 0 0 1 14.12 14.12M16.24 7.76A9.97 9.97 0 0 1 21 12c-1.73 4.11-6 8-11 8a10.02 10.02 0 0 1-5.94-2.06"></path>
                <line x1="1" y1="1" x2="23" y2="23"></line>
              </svg>
            ) : (
              <svg width="20" height="20" fill="none" stroke="#bbb" strokeWidth="2" viewBox="0 0 24 24">
                <ellipse cx="12" cy="12" rx="10" ry="8" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </span>
        </div>

        {/* Remember me & Forgot Password */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 22,
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={remember}
              onChange={e => setRemember(e.target.checked)}
              id="remember"
              style={{ marginRight: 7, cursor: 'pointer' }}
              disabled={loading}
            />
            <label htmlFor="remember" style={{
              fontSize: 15,
              color: '#222',
              userSelect: 'none',
              cursor: 'pointer'
            }}>Remember me</label>
          </div>
          <a
            href="/forgot-password"
            style={{
              fontSize: 15,
              color: '#007bff',
              textDecoration: 'none',
              fontWeight: 500
            }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          >
            Forgot Password?
          </a>
        </div>

        {/* Login Button */}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            background: loading ? '#444' : '#111',
            color: '#fff',
            border: 'none',
            borderRadius: 7,
            fontWeight: 700,
            fontSize: 16,
            padding: '15px 0',
            marginTop: 0,
            letterSpacing: 1,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}>
          {loading ? 'Logging in...' : 'LOGIN IN'}
        </button>
      </form>
    </div>
  );
};

export default LoginPage;
